/** @jsx jsx */
import { jsx } from '@emotion/core'
import _ from 'lodash'
import { BettingListSelector } from '@views/Popup/MessageContentPopup/BettingListSelector'
import { bettingHistoryStore, globalStore, logStore, userPickStore } from '@store/index'
import { UserPickEditor } from '@views/Home/UserPick/UserPickEditor'
import { PAGE_TYPE } from '@interface/common'
import { UserPickContents } from '@views/Home/UserPick/UserPickContents'
import { UserPickGiveawayInfo } from '@views/Home/UserPick/UserPickPage/UserPickGiveawayInfo'
import { PostingActivity } from '@views/MyPage/UserActivity/PostingActivity'
import { LikeActivity } from '@views/MyPage/UserActivity/LikeActivity'
import { FROM_VIEW_TYPE, USER_COMMENT_TYPE } from '@v2/constants/common'
import TodayUserPick from '@views/Home/UserPick/TodayUserPick'
import { UserDetailInfo } from '@views/Popup/UserDetailInfo'
import { CommentActivity } from '@views/MyPage/UserActivity/CommentActivity'
import { UserActivityScore } from '@views/MyPage/UserActivity/UserActivityScore'
import { ActivityHistory } from '@views/MyPage/UserActivity/ActivityHistory'
import { AdvComment } from '@components/AdvComment'
import { Schema$UserComment } from '@interface/comment'
import { AdvCommentEditor } from '@components/AdvComment/AdvCommentEditor'
import { UserBlockList } from '@views/MyPage/UserActivity/UserBlockList'
import { ShareBettingHistory } from '@views/MyPage/UserActivity/ShareBettingHistory'
import PopularSearchTermPopup from '@components/PopularSearchTerm/PopularSearchTermPopup'
import NewsPopup from '@components/PopularSearchTerm/NewsPopup'
import { Schema$PopularSearchTerm } from '@protocol/popularSearchTerm'
import { PopupCloseType } from '@views/Popup/General'
import SportIssuePopup from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/SportIssuePopup'
import AdvImg from '@components/AdvImg'

export const POPUP_IDS = {
  // 나도승부사 경기 분석 팝업창(분석글 팝업에서 동일한 팝업이 한번 더 뜨기 때문에 기존것을 닫기위해)
  USER_PICK_DETAIL_LIST: 'user_pick_detail_list',
}

export default class PopupUtils {

  static showBettingHistory(props: { onClick: () => void, pageType?: PAGE_TYPE }): void {
    let fromViewType = FROM_VIEW_TYPE.BETTING_HISTORY
    const { pageType = null } = props
    switch (pageType) {
      case PAGE_TYPE.USER_PICK:
      case PAGE_TYPE.USER_PICK_DETAIL:
      case PAGE_TYPE.USER_PICK_HOME:
      case PAGE_TYPE.USER_PICK_MATCH_FILTER:
      case PAGE_TYPE.USER_PICK_PROFILE:
        fromViewType = FROM_VIEW_TYPE.USER_PICK
        break
    }

    globalStore.addGeneralPopupProperties(
      <BettingListSelector
        pageType={props.pageType}
        onClick={props.onClick}/>, '베팅 내역',
      {
        wrapClassName: '',
        className: 'betting-list-popup',
        isScroll: false,
        custom: false,
        clsBtn: false,
        closeCallback: () => bettingHistoryStore.resetSelectedBettingHistory(fromViewType),
      },
    )
  }

  /**
   * closeCallback 함수는 우측 상단 X 버튼으로 닫을때 동작 한다.
   * @param fromView
   */
  static showUserPickEditor(fromView: FROM_VIEW_TYPE) {
    globalStore.addGeneralPopupProperties(<UserPickEditor fromView={fromView}/>, '분석글 작성', {
      wrapClassName: '',
      className: '',
      isScroll: false,
      custom: false,
      clsBtn: false,
      isUnableCloseBg: true,
      closeCallback: () => bettingHistoryStore.resetSelectedBettingHistory(fromView),
    })
  }

  static showUserPickList(params?: {
    favoriteMatchCallback?: (isAdded: boolean) => void
    fromView?: FROM_VIEW_TYPE
    targetUserPickId?: string
  }): void {
    globalStore.addGeneralPopupProperties(
      <UserPickContents
        favoriteMatchCallback={params?.favoriteMatchCallback}
        fromView={params?.fromView}
        targetUserPickId={params?.targetUserPickId}
      />,
      '경기 분석', {
        wrapClassName: '',
        className: '',
        isScroll: false,
        custom: false,
        clsBtn: false,
      }, null, POPUP_IDS.USER_PICK_DETAIL_LIST)
  }

  static showUserPickGiveawayInfo(): void {
    globalStore.addGeneralPopupProperties(<UserPickGiveawayInfo/>, '나도 승부사', {
      wrapClassName: '',
      className: '',
      isScroll: false,
      custom: false,
      clsBtn: false,
    })
  }

  static showUserPostingActivity(title: string, baseMonth?: string): void {
    globalStore.addGeneralPopupProperties(<PostingActivity baseMonth={baseMonth}/>, title, {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  static showUserLikeActivity(title: string, params: { baseMonth?: string }): void {
    const { baseMonth } = params
    globalStore.addGeneralPopupProperties(<LikeActivity title={title} baseMonth={baseMonth}/>, title, {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  static showUserCommentActivity(title: string, params: { initTabIndex?: number, baseMonth?: string }): void {
    const { initTabIndex, baseMonth } = params || {}
    globalStore.addGeneralPopupProperties(<CommentActivity initTabIndex={initTabIndex} baseMonth={baseMonth}/>, title, {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  static showUserShareBettingHistory(title: string, params: { baseMonth?: string }): void {
    const { baseMonth } = params
    globalStore.addGeneralPopupProperties(<ShareBettingHistory baseMonth={baseMonth}/>, title, {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  static showTodayUserPick(): void {
    userPickStore.saveLastBestUserPickIdToLocal()
    globalStore.addGeneralPopupProperties(<TodayUserPick/>, '오늘의 승부사', {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
      closeCallback: () => {
        logStore.writeButtonLog({ pageId: 'a050', buttonId: '001' }).catch(console.error)
      }
    })
  }

  static showUserDetailInfo(targetUserPromiseId: string): void {
    const popupId = 'adv-user-detail-info'
    globalStore.closeGeneralPopup(popupId)
    setTimeout(() => {
      bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.USER_DETAIL_INFO)
      bettingHistoryStore.setBettingHistoryInitProps({
        onlyMajorClub: false,
        fromViewType: FROM_VIEW_TYPE.USER_DETAIL_INFO,
      })
      globalStore.addGeneralPopupProperties(<UserDetailInfo targetUserPromiseId={targetUserPromiseId}/>, '프로필', {
        wrapClassName: '',
        className: '',
        isScroll: false,
        custom: false,
        clsBtn: false,
      }, null, popupId)
    }, 1)
  }

  static showUserActivityScore(): void {
    globalStore.addGeneralPopupProperties(<UserActivityScore/>, '활동점수', {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
      closeCallback: () => {
        logStore.writeButtonLog({ pageId: 'e030', buttonId: '001' }).catch(console.error)
      }
    })
  }

  static showActivityScoreListPopup(): void {
    globalStore.addGeneralPopupProperties(<ActivityHistory/>, '점수 내역', {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  /**
   * 팝업의 타이틀을 변경 해준다.
   * @param baseClassName 기준이 되는 클래스명
   * @param changeTitle 변경할 타이틀
   */
  static changePopupTitle(baseClassName: string, changeTitle: string): void {
    const listViewElems = document.querySelectorAll(`.${baseClassName}`)
    let listViewElem: Element
    if (_.size(listViewElems) === 1) {
      listViewElem = listViewElems[0]
    } else { // 동일한 className 으로 2개 이상 팝업이 떠있을 경우 마지막 요소가 현재 보여지는 팝업이다.
      listViewElem = listViewElems[listViewElems.length - 1]
    }
    const content = listViewElem.closest('.am-modal-content')
    if (content) {
      const title = content.querySelector('.am-modal-header .am-modal-title h1.title')
      title.textContent = changeTitle
    }
  }

  static showAdvComments(params: {
    commentType: USER_COMMENT_TYPE, fromViewType: FROM_VIEW_TYPE,
    targetId: string, commentCount: number,
    callbackCreated: (comment: Schema$UserComment) => void,
    callbackDeleted: (comment: Schema$UserComment) => void,
    callbackUpdated?: (comment: Schema$UserComment) => void,
    callbackLiked?: (comment: Schema$UserComment) => void
  }): void {
    // 나도승부사 분석글 댓글과 프리미엄 분석가(TodayPick) 댓글 구분
    const popupId = `adv-comments-popup-${params.fromViewType}`
    globalStore.closeGeneralPopup(popupId)
    setTimeout(() => {
      const contentBody = (
        <AdvComment
          commentType={params.commentType}
          fromViewType={params.fromViewType}
          targetId={params.targetId}
          callbackCreated={params.callbackCreated}
          callbackDeleted={params.callbackDeleted}
          callbackUpdated={params.callbackUpdated}
          callbackLiked={params.callbackLiked}
        />
      )
      let title = `댓글 ${params.commentCount}`
      if (params.commentType === USER_COMMENT_TYPE.COMPANY_PICK_COMMENT) {
        title = `${title}`
      } else if (params.commentType === USER_COMMENT_TYPE.USER_PICK_COMMENT) {
        title = `나도승부사 ${title}`
      }
      globalStore.addGeneralPopupProperties(
        contentBody,
        title,
        {
          wrapClassName: '',
          className: '',
          isScroll: false,
          custom: false,
          clsBtn: false,
          closeCallback: () => bettingHistoryStore.resetSelectedBettingHistory(params.fromViewType),
        }, null, popupId)
    }, 1)
  }

  static showAdvCommentEditor(params: {
    popupId?: string
    className?: string
    commentTypeText?: string
    fromViewType: FROM_VIEW_TYPE
    commentProp: Schema$UserComment
    callbackCreated: (comment: Schema$UserComment) => void
    callbackUpdated: (comment: Schema$UserComment) => void
    callbackClose: () => void
  }): void {
    const contentBody = (
      <AdvCommentEditor
        className={params.className}
        fromViewType={params.fromViewType}
        commentProp={params.commentProp}
        callbackCreated={params.callbackCreated}
        callbackUpdated={params.callbackUpdated}
        callbackClose={params.callbackClose}
        singleMode
      />
    )
    globalStore.addGeneralPopupProperties(contentBody, `${params.commentTypeText} 수정`, {
      wrapClassName: '',
      className: '',
      isScroll: false,
      custom: false,
      clsBtn: false,
    }, null, params.popupId)
  }

  static showBlockManagementPopup(): void {
    globalStore.addGeneralPopupProperties(<UserBlockList/>, '차단관리', {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  static showPopularSearchTermPopup(
    popularSearchTerm: Schema$PopularSearchTerm,
    closeCallback?: (closeType?: PopupCloseType) => void
  ): void {
    globalStore.addGeneralPopupProperties(<PopularSearchTermPopup popularSearchTerm={popularSearchTerm}/>, '이슈 키워드', {
      wrapClassName: '',
      className: 'cls-btn-bottom',
      isScroll: true,
      custom: false,
      clsBtn: true,
      closeCallback,
    })
  }

  static showNewsPopup(link): void {
    globalStore.addGeneralPopupProperties(<NewsPopup link={link}/>, '', {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  static showManagedUserDetailPopup(params: { popupImgUrl: string }): void {
    globalStore.addGeneralPopupProperties(<AdvImg src={params.popupImgUrl} alt=""/>, '상세', {
      wrapClassName: '',
      className: 'full-image-popup',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

  static showSportIssuePopup(matchId: number): void {
    globalStore.addGeneralPopupProperties(<SportIssuePopup matchId={matchId}/>, '대시보드', {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false,
    })
  }

}
